import { API_ENDPOINTS } from '../config/api';
import { fetchWithAuth } from '../utils/authUtils';

// Interfaces para User
export interface User {
  id: number;
  username: string;
  email: string;
  password?: string;
  role: 'ADMIN' | 'USER';
  companyName?: string;
  rut?: string;
  address?: string;
  phone?: string;
  contactName?: string;
  contactPosition?: string;
  active: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  role: 'ADMIN' | 'USER';
  companyName?: string;
  rut?: string;
  address?: string;
  phone?: string;
  contactName?: string;
  contactPosition?: string;
}

export interface UpdateUserData {
  username?: string;
  email?: string;
  password?: string;
  role?: 'ADMIN' | 'USER';
  companyName?: string;
  rut?: string;
  address?: string;
  phone?: string;
  contactName?: string;
  contactPosition?: string;
  active?: boolean;
}

export interface UsersListResponse {
  success: boolean;
  count: number;
  users: User[];
}

export interface UserResponse {
  success: boolean;
  message?: string;
  user: User;
}

export interface ErrorResponse {
  success: false;
  error: string;
}

// Servicio de usuarios
const userService = {
  /**
   * Obtener todos los usuarios
   */
  async getAllUsers(): Promise<UsersListResponse> {
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.users, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error al obtener usuarios' }));
        throw new Error(errorData.error || 'Error al obtener usuarios');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getAllUsers:', error);
      throw error;
    }
  },

  /**
   * Obtener un usuario por ID
   */
  async getUserById(userId: number): Promise<UserResponse> {
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.userById(userId), {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error al obtener usuario' }));
        throw new Error(errorData.error || 'Error al obtener usuario');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getUserById:', error);
      throw error;
    }
  },

  /**
   * Crear un nuevo usuario
   */
  async createUser(userData: CreateUserData): Promise<UserResponse> {
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.users, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error al crear usuario' }));
        throw new Error(errorData.error || 'Error al crear usuario');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en createUser:', error);
      throw error;
    }
  },

  /**
   * Actualizar un usuario existente
   */
  async updateUser(userId: number, userData: UpdateUserData): Promise<UserResponse> {
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.userById(userId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error al actualizar usuario' }));
        throw new Error(errorData.error || 'Error al actualizar usuario');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en updateUser:', error);
      throw error;
    }
  },

  /**
   * Eliminar un usuario
   */
  async deleteUser(userId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetchWithAuth(API_ENDPOINTS.userById(userId), {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error al eliminar usuario' }));
        throw new Error(errorData.error || 'Error al eliminar usuario');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en deleteUser:', error);
      throw error;
    }
  },

  /**
   * Activar/Desactivar un usuario
   */
  async toggleUserStatus(userId: number, active: boolean): Promise<UserResponse> {
    try {
      return await this.updateUser(userId, { active });
    } catch (error) {
      console.error('Error en toggleUserStatus:', error);
      throw error;
    }
  },

  /**
   * Validar email
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /\S+@\S+\.\S+/;
    return emailRegex.test(email);
  },

  /**
   * Validar contraseña
   * Mínimo 8 caracteres, al menos una mayúscula, una minúscula y un número
   */
  isValidPassword(password: string): boolean {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    return passwordRegex.test(password);
  },

  /**
   * Formatear fecha
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  /**
   * Obtener badge de rol
   */
  getRoleBadge(role: string): string {
    return role === 'ADMIN' ? 'Administrador' : 'Usuario';
  },

  /**
   * Obtener color de badge de rol
   */
  getRoleBadgeColor(role: string): string {
    return role === 'ADMIN' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';
  },

  /**
   * Obtener badge de estado
   */
  getStatusBadge(active: boolean): string {
    return active ? 'Activo' : 'Inactivo';
  },

  /**
   * Obtener color de badge de estado
   */
  getStatusBadgeColor(active: boolean): string {
    return active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  }
};

export default userService;
